import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit3,
  FiTrash2,
  FiCopy,
  FiShare2,
  FiBookmark,
  FiCheckCircle,
  FiClock,
  FiUser,
  FiCalendar,
  FiTag,
  FiBarChart2,
  FiXCircle,
  FiAlertCircle,
  FiEye,
  FiThumbsUp,
  FiThumbsDown,
  FiFlag,
  FiMoreVertical
} from 'react-icons/fi';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Badge from '../../components/common/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { questionAPI } from '../../services/api';

const QuestionDetails = () => {
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [feedback, setFeedback] = useState({ helpful: null, comment: '' });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  const { questionId } = useParams();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  
  const loadQuestion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const questionData = await questionAPI.getQuestion(questionId);
      setQuestion(questionData);
      setIsBookmarked(questionData.isBookmarked || false);
    } catch (err) {
      console.error('Failed to load question:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load question details. Please try again later.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [questionId, showError]);

  useEffect(() => {
    if (questionId) {
      loadQuestion();
    }
  }, [questionId, loadQuestion]);
  
  const handleBookmark = async () => {
    try {
      if (isBookmarked) {
        await questionAPI.removeBookmark(questionId);
        showSuccess('Question removed from bookmarks');
      } else {
        await questionAPI.addBookmark(questionId);
        showSuccess('Question bookmarked successfully');
      }
      setIsBookmarked(!isBookmarked);
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      showError('Failed to update bookmark');
    }
  };
  
  const handleCopyQuestion = async () => {
    try {
      const questionText = `${question.title}\n\n${question.content}\n\nOptions:\n${question.options.map((opt, idx) => `${String.fromCharCode(65 + idx)}. ${opt.text}`).join('\n')}`;
      await navigator.clipboard.writeText(questionText);
      showSuccess('Question copied to clipboard');
    } catch (error) {
      showError('Failed to copy question');
    }
  };
  
  const handleShareQuestion = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: question.title,
          text: question.content,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showSuccess('Question link copied to clipboard');
      }
    } catch (error) {
      showError('Failed to share question');
    }
  };
  
  const handleAnswerSelect = (optionId) => {
    setSelectedAnswer(optionId);
    setShowExplanation(true);
  };
  
  const handleFeedback = async (helpful) => {
    try {
      setSubmittingFeedback(true);
      
      await questionAPI.submitFeedback(questionId, {
        helpful,
        comment: feedback.comment
      });
      
      setFeedback({ helpful, comment: feedback.comment });
      showSuccess('Thank you for your feedback!');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      showError('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };
  
  const handleDeleteQuestion = async () => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        await questionAPI.deleteQuestion(questionId);
        showSuccess('Question deleted successfully');
        navigate('/questions');
      } catch (error) {
        console.error('Failed to delete question:', error);
        showError('Failed to delete question');
      }
    }
  };
  
  const canEditQuestion = () => {
    return user?.role === 'admin' || 
           (user?.role === 'teacher' && question?.createdBy?.id === user?.id);
  };
  
  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: 'success',
      intermediate: 'warning',
      advanced: 'danger',
      expert: 'purple'
    };
    return colors[difficulty] || 'info';
  };
  
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return (
      <div className="question-details-loading">
        <LoadingSpinner size="large" />
        <p>Loading question details...</p>
      </div>
    );
  }
  
  if (error && !question) {
    return (
      <div className="question-details-error">
        <FiAlertCircle size={48} />
        <h2>Question Not Found</h2>
        <p>{error}</p>
        <div className="error-actions">
          <Button onClick={() => navigate(-1)} variant="outline">
            Go Back
          </Button>
          <Button onClick={loadQuestion}>Try Again</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="question-details">
      <div className="question-details-container">
        {/* Header */}
        <div className="question-header">
          <div className="header-nav">
            <Button
              variant="text"
              onClick={() => navigate(-1)}
              icon={FiArrowLeft}
            >
              Back
            </Button>
            
            <div className="breadcrumb">
              <Link to="/questions">Questions</Link>
              <span>/</span>
              <span>{question?.subject}</span>
              <span>/</span>
              <span>{question?.topic}</span>
            </div>
          </div>
          
          <div className="header-actions">
            <Button
              variant="text"
              onClick={handleBookmark}
              icon={isBookmarked ? FiCheckCircle : FiBookmark}
              className={isBookmarked ? 'bookmarked' : ''}
            >
              {isBookmarked ? 'Bookmarked' : 'Bookmark'}
            </Button>
            
            <Button
              variant="text"
              onClick={handleCopyQuestion}
              icon={FiCopy}
            >
              Copy
            </Button>
            
            <Button
              variant="text"
              onClick={handleShareQuestion}
              icon={FiShare2}
            >
              Share
            </Button>
            
            {canEditQuestion() && (
              <div className="question-actions">
                <Button
                  variant="text"
                  onClick={() => setShowActions(!showActions)}
                  icon={FiMoreVertical}
                />
                
                {showActions && (
                  <div className="actions-dropdown">
                    <Link
                      to={`/questions/${questionId}/edit`}
                      className="action-item"
                    >
                      <FiEdit3 size={16} />
                      Edit Question
                    </Link>
                    <button
                      className="action-item danger"
                      onClick={handleDeleteQuestion}
                    >
                      <FiTrash2 size={16} />
                      Delete Question
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Question Content */}
        <div className="question-content">
          <div className="question-main">
            {/* Question Info */}
            <div className="question-info">
              <div className="question-meta">
                <Badge variant={getDifficultyColor(question?.difficulty)}>
                  {question?.difficulty}
                </Badge>
                <Badge variant="outline">{question?.type?.replace('_', ' ')}</Badge>
                <div className="question-points">
                  <FiBarChart2 size={14} />
                  {question?.points} points
                </div>
                <div className="question-time">
                  <FiClock size={14} />
                  {formatTime(question?.timeLimit || 0)}
                </div>
              </div>
              
              <h1 className="question-title">{question?.title}</h1>
              
              <div className="question-description">
                <p>{question?.content}</p>
              </div>
              
              {/* Tags */}
              {question?.tags && question.tags.length > 0 && (
                <div className="question-tags">
                  <FiTag size={14} />
                  <div className="tags-list">
                    {question.tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" size="small">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Question Options */}
            {question?.type === 'multiple_choice' && question?.options && (
              <div className="question-options">
                <h3>Choose the correct answer:</h3>
                <div className="options-list">
                  {question.options.map((option, index) => {
                    const isSelected = selectedAnswer === option.id;
                    const isCorrect = option.isCorrect;
                    const showResult = showExplanation;
                    
                    return (
                      <button
                        key={option.id}
                        className={`option-item ${
                          isSelected ? 'selected' : ''
                        } ${
                          showResult && isCorrect ? 'correct' : ''
                        } ${
                          showResult && isSelected && !isCorrect ? 'incorrect' : ''
                        }`}
                        onClick={() => handleAnswerSelect(option.id)}
                        disabled={showExplanation}
                      >
                        <div className="option-label">
                          {String.fromCharCode(65 + index)}
                        </div>
                        <div className="option-text">{option.text}</div>
                        <div className="option-indicator">
                          {showResult && isCorrect && <FiCheckCircle />}
                          {showResult && isSelected && !isCorrect && <FiXCircle />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Explanation */}
            {showExplanation && question?.explanation && (
              <div className="question-explanation">
                <h3>Explanation</h3>
                <div className="explanation-content">
                  <p>{question.explanation.text}</p>
                  
                  {question.explanation.steps && (
                    <div className="explanation-steps">
                      <h4>Step-by-step solution:</h4>
                      <ol>
                        {question.explanation.steps.map((step, index) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Feedback Section */}
            {showExplanation && (
              <div className="question-feedback">
                <h3>Was this explanation helpful?</h3>
                <div className="feedback-actions">
                  <Button
                    variant={feedback.helpful === true ? 'success' : 'outline'}
                    onClick={() => handleFeedback(true)}
                    loading={submittingFeedback}
                    icon={FiThumbsUp}
                  >
                    Yes, helpful
                  </Button>
                  <Button
                    variant={feedback.helpful === false ? 'danger' : 'outline'}
                    onClick={() => handleFeedback(false)}
                    loading={submittingFeedback}
                    icon={FiThumbsDown}
                  >
                    Not helpful
                  </Button>
                </div>
                
                <div className="feedback-comment">
                  <textarea
                    placeholder="Any additional comments? (optional)"
                    value={feedback.comment}
                    onChange={(e) => setFeedback(prev => ({ ...prev, comment: e.target.value }))}
                    className="comment-textarea"
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="question-sidebar">
            {/* Question Stats */}
            <div className="sidebar-card stats-card">
              <h3>Question Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <FiEye className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{question?.stats?.views || 0}</div>
                    <div className="stat-label">Views</div>
                  </div>
                </div>
                <div className="stat-item">
                  <FiBarChart2 className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{question?.stats?.totalAttempts || 0}</div>
                    <div className="stat-label">Attempts</div>
                  </div>
                </div>
                <div className="stat-item">
                  <FiCheckCircle className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{question?.stats?.successRate || 0}%</div>
                    <div className="stat-label">Success Rate</div>
                  </div>
                </div>
                <div className="stat-item">
                  <FiClock className="stat-icon" />
                  <div className="stat-content">
                    <div className="stat-value">{formatTime(question?.stats?.averageTime || 0)}</div>
                    <div className="stat-label">Avg. Time</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Question Details */}
            <div className="sidebar-card details-card">
              <h3>Question Details</h3>
              <div className="details-list">
                <div className="detail-item">
                  <label>Subject:</label>
                  <span>{question?.subject}</span>
                </div>
                <div className="detail-item">
                  <label>Topic:</label>
                  <span>{question?.topic}</span>
                </div>
                {question?.subtopic && (
                  <div className="detail-item">
                    <label>Subtopic:</label>
                    <span>{question?.subtopic}</span>
                  </div>
                )}
                <div className="detail-item">
                  <label>Difficulty:</label>
                  <Badge variant={getDifficultyColor(question?.difficulty)}>
                    {question?.difficulty}
                  </Badge>
                </div>
                <div className="detail-item">
                  <label>Points:</label>
                  <span>{question?.points}</span>
                </div>
                <div className="detail-item">
                  <label>Time Limit:</label>
                  <span>{formatTime(question?.timeLimit || 0)}</span>
                </div>
              </div>
            </div>
            
            {/* Author Info */}
            {question?.createdBy && (
              <div className="sidebar-card author-card">
                <h3>Created By</h3>
                <div className="author-info">
                  <div className="author-avatar">
                    {question.createdBy.avatar ? (
                      <img src={question.createdBy.avatar} alt={question.createdBy.name} />
                    ) : (
                      <FiUser size={24} />
                    )}
                  </div>
                  <div className="author-details">
                    <div className="author-name">{question.createdBy.name}</div>
                    <Badge variant="outline" size="small">
                      {question.createdBy.role}
                    </Badge>
                  </div>
                </div>
                <div className="creation-info">
                  <div className="info-item">
                    <FiCalendar size={14} />
                    <span>Created: {new Date(question.createdAt).toLocaleDateString()}</span>
                  </div>
                  {question.updatedAt !== question.createdAt && (
                    <div className="info-item">
                      <FiCalendar size={14} />
                      <span>Updated: {new Date(question.updatedAt).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Related Questions */}
            {question?.relatedQuestions && question.relatedQuestions.length > 0 && (
              <div className="sidebar-card related-card">
                <h3>Related Questions</h3>
                <div className="related-list">
                  {question.relatedQuestions.map((related) => (
                    <Link
                      key={related.id}
                      to={`/questions/${related.id}`}
                      className="related-item"
                    >
                      <div className="related-content">
                        <div className="related-title">{related.title}</div>
                        <div className="related-meta">
                          <Badge variant={getDifficultyColor(related.difficulty)} size="small">
                            {related.difficulty}
                          </Badge>
                          <span className="related-subject">{related.subject}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            {/* Report Question */}
            <div className="sidebar-card report-card">
              <Button
                variant="outline"
                size="small"
                icon={FiFlag}
                className="report-btn"
              >
                Report Question
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionDetails;